import i18n from "locale/i18n";
import { toast } from "react-toastify";

const FIELD_LABEL_MAP: Record<string, { en: string; id: string }> = {
  npwp: {
    en: "NPWP",
    id: "NPWP",
  },
  vendor_code: {
    en: "Vendor code",
    id: "Kode vendor",
  },
  vendor_name: {
    en: "Vendor name",
    id: "Nama vendor",
  },
  customer_name: {
    en: "Customer name",
    id: "Nama pelanggan",
  },
  ktp: {
    en: "KTP",
    id: "KTP",
  },
  stnk: {
    en: "STNK",
    id: "STNK",
  },
  kir: {
    en: "KIR",
    id: "KIR",
  },
  sim: {
    en: "SIM",
    id: "SIM",
  },
  license_expiry_date: {
    en: "License expiry date",
    id: "Tanggal kedaluwarsa SIM",
  },
  license_number: {
    en: "License ID",
    id: "Nomor SIM",
  },
  license_type: {
    en: "License type",
    id: "Tipe SIM",
  },
  full_name: {
    en: "Full name",
    id: "Nama lengkap",
  },
  driver_name: {
    en: "Driver name",
    id: "Nama pengemudi",
  },
  ktp_number: {
    en: "KTP number",
    id: "Nomor KTP",
  },
  phone_number: {
    en: "Phone number",
    id: "Nomor telepon",
  },
  email: {
    en: "Email",
    id: "Email",
  },
  gender: {
    en: "Gender",
    id: "Jenis kelamin",
  },
  marital_status: {
    en: "Marital status",
    id: "Status pernikahan",
  },
  vendor_id: {
    en: "Associated vendor",
    id: "Vendor terkait",
  },
  domicile_address: {
    en: "Domicile address",
    id: "Alamat domisili",
  },
  citizen_address: {
    en: "Citizen address",
    id: "Alamat KTP",
  },
  bank_name: {
    en: "Bank name",
    id: "Nama bank",
  },
  bank_account_number: {
    en: "Bank account number",
    id: "Nomor rekening",
  },
  bank_account_holder: {
    en: "Bank account holder",
    id: "Nama pemilik rekening",
  },
  emergency_contact_name: {
    en: "Emergency contact name",
    id: "Nama kontak darurat",
  },
  emergency_contact_phone: {
    en: "Emergency contact phone",
    id: "Nomor telepon kontak darurat",
  },
  emergency_contact_relation: {
    en: "Emergency contact relation",
    id: "Hubungan kontak darurat",
  },
  join_date: {
    en: "Join date",
    id: "Tanggal bergabung",
  },
  plate_number: {
    en: "Plate number",
    id: "Nomor polisi",
  },
  vehicle_type_id: {
    en: "Vehicle type",
    id: "Tipe kendaraan",
  },
  vehicle_model_id: {
    en: "Vehicle model",
    id: "Model kendaraan",
  },
  stnk_expiry_date: {
    en: "STNK expiry date",
    id: "Masa berlaku STNK",
  },
  kir_expiry_date: {
    en: "KIR expiry date",
    id: "Masa berlaku KIR",
  },
  last_maintenance_date: {
    en: "Last maintenance date",
    id: "Tanggal servis terakhir",
  },
  expiry_date: {
    en: "Expiry date",
    id: "Tanggal kedaluwarsa",
  },
};

const VALIDATION_PHRASES_ID: Array<{ pattern: RegExp; replacement: string }> = [
  {
    pattern: /\bmust be a future date\b/gi,
    replacement: "harus merupakan tanggal di masa depan",
  },
  {
    pattern: /\bmust be in the future\b/gi,
    replacement: "harus merupakan tanggal di masa depan",
  },
  {
    pattern: /\bmust be a past date\b/gi,
    replacement: "harus merupakan tanggal di masa lalu",
  },
  {
    pattern: /\bmust be in the past\b/gi,
    replacement: "harus merupakan tanggal di masa lalu",
  },
  {
    pattern: /\bis required\b/gi,
    replacement: "wajib diisi",
  },
  {
    pattern: /\bcannot be empty\b/gi,
    replacement: "tidak boleh kosong",
  },
  {
    pattern: /\bcannot be blank\b/gi,
    replacement: "tidak boleh kosong",
  },
  {
    pattern: /\balready exists\b/gi,
    replacement: "sudah terdaftar",
  },
  {
    pattern: /\bis already exist\b/gi,
    replacement: "sudah terdaftar",
  },
];

export const normalizeErrorMessage = (rawMessage?: string): string => {
  if (!rawMessage || typeof rawMessage !== "string") return "Unknown Error";
  let msg = rawMessage.trim();
  if (!msg) return "Unknown Error";

  const lang = (i18n.language || "en").toLowerCase().startsWith("id") ? "id" : "en";

  // Replace known snake_case field keys
  Object.keys(FIELD_LABEL_MAP).forEach((fieldKey) => {
    const regex = new RegExp(`\\b${fieldKey}\\b`, "gi");
    if (regex.test(msg)) {
      const fieldLabel = FIELD_LABEL_MAP[fieldKey][lang];
      msg = msg.replace(regex, fieldLabel);
    }
  });

  // Humanize any other snake_case field names
  msg = msg.replace(/\b([a-z]+(?:_[a-z0-9]+)+)\b/gi, (match) => {
    const key = match.toLowerCase();
    if (FIELD_LABEL_MAP[key]) {
      return FIELD_LABEL_MAP[key][lang];
    }
    return match.replace(/_/g, " ");
  });

  // If Indonesian, translate common validation predicates
  if (lang === "id") {
    VALIDATION_PHRASES_ID.forEach(({ pattern, replacement }) => {
      msg = msg.replace(pattern, replacement);
    });
  }

  // Capitalize first letter
  return msg.charAt(0).toUpperCase() + msg.slice(1);
};

export const errorHandler = (error: unknown) => {
  if (error && typeof error === "object") {
    const err = error as any;

    // 1. FetchBaseQueryError data message
    if (err.data && typeof err.data === "object") {
      const msg = err.data.message || err.data.error || err.data.msg;
      if (typeof msg === "string") {
        toast.error(normalizeErrorMessage(msg));
        return;
      }
    }

    // 2. FetchBaseQueryError data string
    if (typeof err.data === "string") {
      toast.error(normalizeErrorMessage(err.data));
      return;
    }

    // 3. SerializedError message or standard error message
    if (typeof err.message === "string") {
      toast.error(normalizeErrorMessage(err.message));
      return;
    }

    // 4. FetchBaseQueryError error string
    if (typeof err.error === "string") {
      toast.error(normalizeErrorMessage(err.error));
      return;
    }
  }

  toast.error("Unknown Error");
};
